import {
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { FieldErrors } from "react-hook-form";

import {
  formatVerificationOutcome,
  useVerificationColors,
} from "../../../utils/verification";
import type { InsuranceVerificationFormFields } from "../form/validation.zod";
import ValidationDisplay from "./ValidationDisplay";

interface CompletionModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  selectedOutcome: string;
  setSelectedOutcome: (outcome: string) => void;
  predictedOutcome: string;
  formErrors: FieldErrors<InsuranceVerificationFormFields>;
  validationWarnings: string[];
  verificationMethod: string | null;
  setVerificationMethod: (method: string | null) => void;
  callReferenceNumber: string | null;
  setCallReferenceNumber: (reference: string | null) => void;
  hasProof: boolean;
  onUploadProof: () => void;
}

const PROOF_REQUIRED_METHODS = ["PAYER_PORTAL", "FAX"];

const METHOD_REQUIRED_OUTCOMES = [
  "COVERAGE_ACTIVE_HAS_ORTHO",
  "COVERAGE_ACTIVE_NO_ORTHO",
  "COVERAGE_INACTIVE",
];

export default function CompletionModal({
  open,
  onClose,
  onComplete,
  selectedStatus,
  setSelectedStatus,
  selectedOutcome,
  setSelectedOutcome,
  predictedOutcome,
  formErrors,
  validationWarnings,
  verificationMethod,
  setVerificationMethod,
  callReferenceNumber,
  setCallReferenceNumber,
  hasProof,
  onUploadProof,
}: CompletionModalProps) {
  const { getStatusColor, getOutcomeColor } = useVerificationColors();

  const hasErrors = Object.keys(formErrors).length > 0;
  const hasWarnings = validationWarnings.length > 0;

  const methodRequired =
    selectedStatus === "COMPLETED" &&
    METHOD_REQUIRED_OUTCOMES.includes(selectedOutcome);
  const isPhoneCall = verificationMethod === "PHONE_CALL";
  const missingMethod = methodRequired && !verificationMethod;
  const missingReference =
    methodRequired && isPhoneCall && !callReferenceNumber?.trim();
  const missingProof =
    methodRequired &&
    !!verificationMethod &&
    PROOF_REQUIRED_METHODS.includes(verificationMethod) &&
    !hasProof;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Verification</DialogTitle>
      <DialogContent>
        {/* Auto-calculated prediction */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Auto-calculated outcome:</strong>{" "}
            {formatVerificationOutcome(predictedOutcome)}
            <br />
            You can modify the values below if needed before completing.
          </Typography>
        </Alert>

        {/* Verification Status Dropdown */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
            Verification Status
          </FormLabel>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              borderRadius: "12px",
              color: getStatusColor(selectedStatus),
            }}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              backgroundColor: "#e8f5e8",
            }}
          >
            <MenuItem
              value="QUEUED"
              style={{ color: getStatusColor("QUEUED") }}
            >
              Queued
            </MenuItem>
            <MenuItem
              value="IN_PROGRESS"
              style={{ color: getStatusColor("IN_PROGRESS") }}
            >
              In Progress
            </MenuItem>
            <MenuItem
              value="REVIEW"
              style={{ color: getStatusColor("REVIEW") }}
            >
              Review
            </MenuItem>
            <MenuItem
              value="COMPLETED"
              style={{ color: getStatusColor("COMPLETED") }}
            >
              Completed
            </MenuItem>
            <MenuItem
              value="SKIPPED"
              style={{ color: getStatusColor("SKIPPED") }}
            >
              Skipped
            </MenuItem>
          </Select>
        </FormControl>

        {/* Verification Outcome Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
            Verification Outcome
          </FormLabel>
          <Select
            value={selectedOutcome}
            onChange={(e) => setSelectedOutcome(e.target.value)}
            style={{
              borderRadius: "12px",
              color: getOutcomeColor(selectedOutcome),
            }}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              backgroundColor: "#e8f5e8",
            }}
          >
            <MenuItem
              value="COVERAGE_ACTIVE_HAS_ORTHO"
              style={{
                color: getOutcomeColor("COVERAGE_ACTIVE_HAS_ORTHO"),
              }}
            >
              Coverage Active - Ortho Benefit
            </MenuItem>
            <MenuItem
              value="COVERAGE_ACTIVE_NO_ORTHO"
              style={{
                color: getOutcomeColor("COVERAGE_ACTIVE_NO_ORTHO"),
              }}
            >
              Coverage Active - No Ortho
            </MenuItem>
            <MenuItem
              value="COVERAGE_INACTIVE"
              style={{
                color: getOutcomeColor("COVERAGE_INACTIVE"),
              }}
            >
              Coverage Inactive
            </MenuItem>
            <MenuItem
              value="CARRIER_UNAVAILABLE"
              style={{
                color: getOutcomeColor("CARRIER_UNAVAILABLE"),
              }}
            >
              Carrier Unavailable
            </MenuItem>
            <MenuItem
              value="PATIENT_NOT_FOUND"
              style={{
                color: getOutcomeColor("PATIENT_NOT_FOUND"),
              }}
            >
              Patient Not Found
            </MenuItem>
            <MenuItem
              value="ALREADY_VERIFIED"
              style={{
                color: getOutcomeColor("ALREADY_VERIFIED"),
              }}
            >
              Already Verified
            </MenuItem>
            <MenuItem
              value="NOT_REQUIRED"
              style={{ color: getOutcomeColor("NOT_REQUIRED") }}
            >
              Not Required
            </MenuItem>
            <MenuItem
              value="SECONDARY_INSURANCE"
              style={{
                color: getOutcomeColor("SECONDARY_INSURANCE"),
              }}
            >
              Secondary Insurance
            </MenuItem>
            <MenuItem
              value="INSUFFICIENT_DATA"
              style={{
                color: getOutcomeColor("INSUFFICIENT_DATA"),
              }}
            >
              Insufficient Data
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
            Verification Method{methodRequired ? " *" : ""}
          </FormLabel>
          <Select
            value={verificationMethod || ""}
            onChange={(e) => setVerificationMethod(e.target.value || null)}
            displayEmpty
            data-testid="verificationMethod"
            style={{
              borderRadius: "12px",
            }}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              backgroundColor: "#f5f5f5",
            }}
          >
            <MenuItem value="">
              <em>Select a method</em>
            </MenuItem>
            <MenuItem value="PAYER_PORTAL">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LanguageIcon fontSize="small" />
                Payer Portal
              </Box>
            </MenuItem>
            <MenuItem value="PHONE_CALL">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" />
                Phone Call
              </Box>
            </MenuItem>
            <MenuItem value="FAX">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PrintIcon fontSize="small" />
                Fax
              </Box>
            </MenuItem>
          </Select>
          {missingMethod && (
            <FormHelperText error data-testid="verificationMethodError">
              Select how this verification was completed
            </FormHelperText>
          )}
        </FormControl>

        {missingProof && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={onUploadProof}
                data-testid="uploadProof"
              >
                Upload proof
              </Button>
            }
          >
            Upload the verification proof (PDF) to complete a{" "}
            {verificationMethod === "FAX" ? "fax" : "payer portal"}{" "}
            verification.
          </Alert>
        )}

        {isPhoneCall && (
          <TextField
            fullWidth
            label={
              methodRequired
                ? "Call Reference Number *"
                : "Call Reference Number"
            }
            value={callReferenceNumber ?? ""}
            onChange={(e) => setCallReferenceNumber(e.target.value || null)}
            error={missingReference}
            helperText={
              missingReference
                ? "Enter the reference number given on the call"
                : undefined
            }
            inputProps={{
              maxLength: 255,
              "data-testid": "completionCallReferenceNumber",
            }}
            sx={{ mb: 3 }}
          />
        )}

        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "#f0f9f0",
            border: "1px solid #c8e6c9",
            "& .MuiAlert-icon": {
              color: "#4caf50",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            What happens next:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              PDF verification document will be generated and this verification
              will be ready to be posted to the PMS
            </Typography>
          </Box>
        </Alert>
        <ValidationDisplay
          formErrors={formErrors}
          validationWarnings={validationWarnings}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={onComplete}
          color="primary"
          variant="contained"
          disabled={
            hasErrors || missingMethod || missingReference || missingProof
          }
          data-testid="saveAndComplete"
        >
          {hasWarnings ? "Continue with Warnings" : "Save & Complete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
